/**
 * RequiredDocsDialog — Guía de documentos necesarios para solicitar reembolso.
 *
 * Pantalla 1: lista de 6 tipos de prestación (Accidente, Enfermedad, Terapia,
 * Vacunas, Exámenes, Consulta).
 * Pantalla 2: documentos requeridos para el tipo elegido, numerados con badges
 * Obligatorio/Opcional.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@shared/utils/cn';
import {
  Bandage,
  Thermometer,
  Sparkles,
  Syringe,
  TestTube,
  Stethoscope,
  ChevronRight,
  ChevronLeft,
  Download,
  type LucideIcon,
} from 'lucide-react';

type PrestationKey =
  | 'accidente'
  | 'enfermedad'
  | 'terapia'
  | 'vacunas'
  | 'examenes'
  | 'consulta';

interface PrestationOption {
  key: PrestationKey;
  title: string;
  subtitle: string;
  icon: LucideIcon;
}

const PRESTATIONS: PrestationOption[] = [
  { key: 'accidente', title: 'Accidente', subtitle: 'Tu mascota tuvo un accidente', icon: Bandage },
  { key: 'enfermedad', title: 'Enfermedad', subtitle: 'Tu mascota está enferma', icon: Thermometer },
  { key: 'terapia', title: 'Terapia complementaria', subtitle: 'Terapias adicionales de recuperación', icon: Sparkles },
  { key: 'vacunas', title: 'Vacunas preventivas', subtitle: 'Vacunación anual', icon: Syringe },
  { key: 'examenes', title: 'Exámenes preventivos', subtitle: 'Exámenes de rutina', icon: TestTube },
  { key: 'consulta', title: 'Consulta preventiva', subtitle: 'Consulta de control', icon: Stethoscope },
];

interface DocItem {
  title: string;
  required: 'mandatory' | 'optional';
  description?: string;
  template?: boolean;
  warning?: string;
}

const DOCS_BY_PRESTATION: Record<PrestationKey, DocItem[]> = {
  accidente: [
    { title: 'Boleta o factura', required: 'mandatory', description: 'Comprobante original del servicio veterinario.' },
    { title: 'Informe clínico', required: 'mandatory', description: 'Detalle del diagnóstico y tratamiento aplicado.', template: true },
    {
      title: 'Presupuesto',
      required: 'optional',
      description: 'Solo en caso de hospitalización o cirugías programadas.',
      template: true,
      warning: 'Si el monto supera las 5 UF, el presupuesto pasa a ser obligatorio.',
    },
  ],
  enfermedad: [
    { title: 'Boleta o factura', required: 'mandatory', description: 'Comprobante original del servicio veterinario.' },
    { title: 'Informe clínico', required: 'mandatory', description: 'Diagnóstico completo y plan de tratamiento.', template: true },
    {
      title: 'Exámenes complementarios',
      required: 'optional',
      description: 'Resultados de exámenes que respalden el diagnóstico.',
    },
  ],
  terapia: [
    { title: 'Boleta o factura', required: 'mandatory', description: 'Comprobante de cada sesión.' },
    { title: 'Informe clínico de derivación', required: 'mandatory', description: 'Indicación médica que justifique la terapia.', template: true },
    { title: 'Plan de tratamiento', required: 'optional', description: 'Detalle de sesiones previstas.' },
  ],
  vacunas: [
    { title: 'Boleta o factura', required: 'mandatory', description: 'Comprobante con detalle de las vacunas aplicadas.' },
    { title: 'Carnet de vacunación actualizado', required: 'mandatory', description: 'Foto clara del carnet con sellos.' },
  ],
  examenes: [
    { title: 'Boleta o factura', required: 'mandatory', description: 'Comprobante del laboratorio o clínica.' },
    { title: 'Orden médica', required: 'mandatory', description: 'Indicación del veterinario que solicitó los exámenes.', template: true },
    { title: 'Resultados', required: 'optional', description: 'Resultados obtenidos (acelera la revisión).' },
  ],
  consulta: [
    { title: 'Boleta o factura', required: 'mandatory', description: 'Comprobante de la consulta veterinaria.' },
    { title: 'Informe clínico', required: 'optional', description: 'Resumen de la consulta firmado por el veterinario.', template: true },
  ],
};

interface RequiredDocsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RequiredDocsDialog({ open, onOpenChange }: RequiredDocsDialogProps) {
  const [selected, setSelected] = useState<PrestationKey | null>(null);

  const handleClose = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      // reset state al cerrar para empezar limpio la próxima vez
      setTimeout(() => setSelected(null), 200);
    }
  };

  const selectedPrestation = selected ? PRESTATIONS.find((p) => p.key === selected) : null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden p-0 gap-0">
        {!selected ? (
          <>
            <DialogHeader className="px-6 pt-6 pb-2">
              <DialogTitle className="text-xl font-bold tracking-tight">
                ¿Qué documentos necesito?
              </DialogTitle>
              <DialogDescription>
                Elegí el tipo de prestación para ver los documentos requeridos.
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] px-6">
              <div className="flex flex-col gap-2 py-2">
                {PRESTATIONS.map(({ key, title, subtitle, icon: Icon }) => (
                  <button
                    type="button"
                    key={key}
                    onClick={() => setSelected(key)}
                    className={cn(
                      'flex items-center gap-3 rounded-xl border border-border bg-card p-3.5 text-left',
                      'transition-all hover:shadow-md hover:border-foreground/15',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    )}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                      <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground">{title}</p>
                      <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
                    </div>
                    <ChevronRight
                      className="h-4 w-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </button>
                ))}
              </div>
            </ScrollArea>
            <div className="border-t border-border px-6 py-4">
              <Button
                className="w-full rounded-full"
                onClick={() => handleClose(false)}
              >
                Entendido
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="px-6 pt-6 pb-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Volver"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                </button>
                <div>
                  <DialogTitle className="text-xl font-bold tracking-tight">
                    Documentos necesarios
                  </DialogTitle>
                  <DialogDescription>{selectedPrestation?.title}</DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] px-6">
              <ol className="flex flex-col gap-3 py-2">
                {DOCS_BY_PRESTATION[selected].map((doc, idx) => (
                  <li
                    key={doc.title}
                    className="rounded-xl border border-border bg-card p-4"
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sidebar-primary text-xs font-bold text-sidebar-primary-foreground">
                        {idx + 1}
                      </span>
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-foreground">{doc.title}</p>
                          <span
                            className={cn(
                              'rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                              doc.required === 'mandatory'
                                ? 'border-rose-200 bg-rose-50 text-rose-700'
                                : 'border-border bg-muted text-muted-foreground',
                            )}
                          >
                            {doc.required === 'mandatory' ? 'Obligatorio' : 'Opcional'}
                          </span>
                        </div>
                        {doc.description && (
                          <p className="text-xs text-muted-foreground">{doc.description}</p>
                        )}
                        {doc.warning && (
                          <p className="rounded-lg bg-chart-4/15 px-3 py-2 text-xs text-foreground/80">
                            <span className="font-semibold">¡Importante!</span> {doc.warning}
                          </p>
                        )}
                        {doc.template && (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-xs font-medium text-sidebar-primary hover:underline"
                          >
                            <Download className="h-3.5 w-3.5" aria-hidden="true" />
                            Descargar plantilla
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </ScrollArea>
            <div className="border-t border-border px-6 py-4">
              <Button
                className="w-full rounded-full"
                onClick={() => handleClose(false)}
              >
                Entendido
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
